import React from "react";
import { Stack, Alert, Text } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import {
  BusinessBasicsData,
  BUSINESS_BASICS_QUESTIONS,
  QUESTION_KEYS,
} from "../types/onboarding";
import ChipGroupSingle from "../components/ChipGroupSingle";
import styles from "./BusinessBasicsStepContent.module.css";

interface BusinessBasicsStepContentProps {
  data: BusinessBasicsData;
  onChange: (
    data: Partial<BusinessBasicsData>,
    questionKey?: string,
    questionValue?: string,
    answerValue?: string,
  ) => void;
}

export default function BusinessBasicsStepContent({
  data,
  onChange,
}: BusinessBasicsStepContentProps) {
  const locationCountQuestion = BUSINESS_BASICS_QUESTIONS.find(
    (q) => q.questionKey === QUESTION_KEYS.HOW_MANY_LOCATIONS_DO_YOU_HAVE,
  );
  const revenueRangeQuestion = BUSINESS_BASICS_QUESTIONS.find(
    (q) =>
      q.questionKey === QUESTION_KEYS.WHAT_IS_YOUR_ESTIMATED_ANNUAL_REVENUE,
  );
  const currentSolutionQuestion = BUSINESS_BASICS_QUESTIONS.find(
    (q) => q.questionKey === QUESTION_KEYS.WHAT_IS_YOUR_CURRENT_SOLUTION,
  );

  const showMultiLocationNotice =
    data.locationCount && data.locationCount !== "1";

  return (
    <Stack className={styles.container}>
      <Stack gap="xl" className={styles.scrollableContent}>
        {locationCountQuestion && (
          <ChipGroupSingle
            label={locationCountQuestion.questionText}
            options={
              locationCountQuestion.options?.map((opt) => ({
                value: opt,
                label: opt,
              })) || []
            }
            value={data.locationCount}
            onChange={(value) =>
              onChange(
                { locationCount: value },
                QUESTION_KEYS.HOW_MANY_LOCATIONS_DO_YOU_HAVE,
                locationCountQuestion.questionText,
                value || "",
              )
            }
          />
        )}

        {showMultiLocationNotice && (
          <Alert
            variant="light"
            color="yellow"
            icon={<IconInfoCircle size={20} />}
            className={styles.notice}
            styles={{
              root: {
                backgroundColor: "#fffbec",
                border: "none",
              },
            }}
          >
            <Text fw={600} size="sm">
              Please make an account for your main location
            </Text>
            <Text size="sm" c="dimmed">
              One of our sales representatives will be in touch shortly to
              discuss multi-location support with you.
            </Text>
          </Alert>
        )}

        {revenueRangeQuestion && (
          <ChipGroupSingle
            label={revenueRangeQuestion.questionText}
            options={
              revenueRangeQuestion.options?.map((opt) => ({
                value: opt,
                label: opt,
              })) || []
            }
            value={data.revenueRange}
            onChange={(value) =>
              onChange(
                { revenueRange: value },
                QUESTION_KEYS.WHAT_IS_YOUR_ESTIMATED_ANNUAL_REVENUE,
                revenueRangeQuestion.questionText,
                value || "",
              )
            }
          />
        )}

        {currentSolutionQuestion && (
          <ChipGroupSingle
            label={currentSolutionQuestion.questionText}
            options={
              currentSolutionQuestion.options?.map((opt) => ({
                value: opt,
                label: opt,
              })) || []
            }
            value={data.currentSolution}
            onChange={(value) =>
              onChange(
                { currentSolution: value },
                QUESTION_KEYS.WHAT_IS_YOUR_CURRENT_SOLUTION,
                currentSolutionQuestion.questionText,
                value || "",
              )
            }
          />
        )}
      </Stack>
    </Stack>
  );
}
